import { useCallback, useEffect, useState } from "react";
import { Box, Button, ButtonGroup, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import ReminderEditModal from "./ReminderEditModal.jsx";
import StatusList from "./StatusList.jsx";
import WorkflowDiagram from "./WorkflowDiagram.jsx";

const VIEWS = [
  { value: "list", label: "Statusliste" },
  { value: "diagram", label: "Diagramm" },
];

export default function ClaimWorkflowPage() {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const [workflow, setWorkflow] = useState(null);
  const [view, setView] = useState("list");
  // { id } bearbeitet eine Erinnerung, { new_status } erstellt eine neue für diesen Status
  const [editing_reminder, setEditingReminder] = useState(null);

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Einstellungen", path: "/admin/settings" },
      { name: "Status-Workflow", path: "/admin/workflow" },
    ]);
  }, [setBreadcrumbs]);

  const fetchWorkflow = useCallback(
    () =>
      backendGet("data/claim-workflow", {
        label: "Status-Workflow",
        enqueueSnackbar,
        setResult: (res) => setWorkflow(res.data),
      }),
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  const label_of = (value) =>
    workflow?.states.find((state) => state.value === value)?.label ?? value;

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Typography variant="h5">Status-Workflow</Typography>
        <ButtonGroup size="small" aria-label="Darstellung">
          {VIEWS.map(({ value, label }) => (
            <Button
              key={value}
              variant={view === value ? "contained" : "outlined"}
              onClick={() => setView(value)}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {view === "diagram" && workflow && (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Die Status haben die Farben der Schadenliste, rote Linien sind
            Rücksprünge. Die Details zu Wechseln, Aktionen und Erinnerungen
            stehen in der Statusliste.
          </Typography>
          <WorkflowDiagram
            states={workflow.states}
            transitions={workflow.transitions}
          />
        </>
      )}

      {view === "list" && workflow && (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Je Status die möglichen Wechsel. Fett gedruckte Wechsel lösen
            automatisch Aktionen aus (Klick zeigt Details), Erinnerungen stehen
            rechts am Status. Überfahren eines Wechsels zeigt die Verbindung zum
            Status, Klick auf den Pfeil springt dorthin.
          </Typography>
          <Box maxWidth={900} mx="auto">
            <StatusList
              states={workflow.states}
              transitions={workflow.transitions}
              label_of={label_of}
              onEditReminder={(id) => setEditingReminder({ id })}
              onCreateReminder={(new_status) =>
                setEditingReminder({ new_status })
              }
            />
          </Box>
        </>
      )}
      {editing_reminder && (
        <ReminderEditModal
          key={editing_reminder.id ?? editing_reminder.new_status}
          reminder_id={editing_reminder.id}
          new_status={editing_reminder.new_status}
          states={Object.fromEntries(
            workflow.states.map((state) => [state.value, state.label])
          )}
          onClose={() => setEditingReminder(null)}
          onSaved={fetchWorkflow}
        />
      )}
    </>
  );
}
